import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Check, CreditCard, Loader2, FileSearch } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const pricingTable = [
  {
    service: "Analyse d'annonce",
    price: "49 €",
    priceValue: 49,
    description: "Une analyse indépendante de jusqu'à 3 annonces repérées : cohérence du prix, points de vigilance, questions à poser au vendeur",
    highlighted: true,
    ctaLabel: "Faire analyser une annonce",
    ctaAction: "route",
    ctaRoute: "/analyse-annonce-bateau",
    badge: "NOUVEAU",
    icon: FileSearch,
  },
  {
    service: "Appel découverte",
    price: "GRATUIT",
    priceValue: 0,
    description: "Discussion de votre projet sans engagement",
    ctaLabel: "Réserver",
    ctaAction: "contact",
  },
  {
    service: "Consultation conseil",
    price: "150 €",
    priceValue: 150,
    description: "Conseils personnalisés sur votre projet",
    lookup_key: "consultation-conseil-eur",
  },
  {
    service: "Recherche bateau sur mesure",
    price: "1 200 €",
    priceValue: 1200,
    description: "Identification et sélection de bateaux correspondant à vos critères",
    lookup_key: "recherche-bateau-eur",
  },
  {
    service: "Audit pré-achat à distance",
    price: "350 €",
    priceValue: 350,
    description: "Analyse détaillée photos/vidéos + rapport complet",
    lookup_key: "audit-distance-eur",
  },
  {
    service: "Audit pré-achat sur place",
    price: "600 €",
    priceValue: 600,
    description: "Déplacement + inspection complète + rapport détaillé",
    lookup_key: "audit-sur-place-eur",
  },
  {
    service: "Négociation seule",
    price: "25% de l'économie",
    description: "Je négocie pour obtenir le meilleur prix",
    ctaLabel: "Nous contacter",
    ctaAction: "contact",
  },
  {
    service: "Pack complet",
    price: "2 500 €",
    priceValue: 2500,
    description: "Recherche + audit + négociation + accompagnement administratif",
    lookup_key: "pack-complet-eur",
    highlighted: true,
  },
  {
    service: "Formation primo-accédant",
    price: "400 €",
    priceValue: 400,
    description: "Coaching complet pour votre premier bateau",
    lookup_key: "formation-primo-eur",
  },
];

const PricingSection = () => {
  const [loadingKey, setLoadingKey] = useState(null);
  const [annonceCfg, setAnnonceCfg] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_URL}/api/analyse-annonce/config`)
      .then((r) => setAnnonceCfg(r.data))
      .catch(() => {});
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleBuy = async (item) => {
    if (item.ctaAction === 'route' && item.ctaRoute) {
      navigate(item.ctaRoute);
      return;
    }
    if (item.ctaAction === 'contact' || !item.lookup_key) {
      scrollToContact();
      return;
    }
    try {
      setLoadingKey(item.lookup_key);
      const { data } = await axios.post(`${API_URL}/api/payments/checkout`, {
        lookup_key: item.lookup_key,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('URL de paiement manquante');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Erreur inconnue';
      alert(`Impossible de démarrer le paiement : ${detail}`);
      setLoadingKey(null);
    }
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 md:py-32 bg-[#1a1c1b]">
      <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
        <div className="text-center mb-16">
          <h2 className="font-black text-5xl md:text-7xl leading-tight text-[#d9fb06] uppercase mb-6">
            Tarifs
          </h2>
          <p className="text-xl text-[#888680] max-w-3xl mx-auto">
            Des prestations transparentes adaptées à vos besoins
          </p>
        </div>

        {/* Pricing Table */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-16">
          {pricingTable.map((item) => {
            const isFree = item.price === 'GRATUIT';
            const isRoute = item.ctaAction === 'route';
            const isCustom = !item.lookup_key && !isFree && !isRoute;
            const isLoading = loadingKey === item.lookup_key;
            const IconEl = item.icon;
            // Dynamic price + free/paid state for analyse-annonce
            const isAnnonce = item.ctaAction === 'route' && item.ctaRoute === '/analyse-annonce-bateau';
            const annoncePriceEur = annonceCfg?.price_cents ? annonceCfg.price_cents / 100 : 49;
            const annonceFree = isAnnonce && annonceCfg?.is_free_available;
            const displayPrice = isAnnonce
              ? (annonceFree
                  ? 'OFFERT'
                  : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(annoncePriceEur))
              : item.price;
            return (
              <Card
                key={item.service}
                data-testid={`pricing-card-${item.lookup_key || item.ctaRoute || item.service}`}
                className={`${
                  item.highlighted
                    ? 'bg-[#3f4816] border-2 border-[#d9fb06] shadow-lg shadow-[#d9fb06]/20'
                    : 'bg-[#302f2c] border border-[#3f4816]'
                } p-6 hover:border-[#d9fb06] transition-all duration-300 flex flex-col relative`}
              >
                {item.badge && (
                  <span className="absolute -top-3 left-6 bg-[#d9fb06] text-[#1a1c1b] text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider">
                    {item.badge}
                  </span>
                )}
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 flex-1">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#d9fb06] mb-2 uppercase flex items-center gap-2">
                      {IconEl && <IconEl size={18} />}
                      {item.service}
                    </h3>
                    <p className="text-[#888680] text-sm mb-2">{item.description}</p>
                    {isAnnonce && annonceFree && (
                      <p className="text-[#d9fb06] text-xs font-bold mt-1">
                        Il reste {annonceCfg?.free_remaining} gratuité{annonceCfg?.free_remaining > 1 ? 's' : ''} — opération de lancement
                      </p>
                    )}
                  </div>
                  <div className="md:text-right md:min-w-[110px]">
                    <p className={`${item.highlighted ? 'text-3xl' : 'text-2xl'} font-black text-[#d9fb06]`}>
                      {displayPrice}
                    </p>
                    {isAnnonce && (
                      <p className="text-[#dfddd6] text-xs font-semibold mt-1" data-testid="annonce-pack-note">
                        {annonceFree ? `puis ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(annoncePriceEur)} pour 3 annonces` : 'pour 3 annonces'}
                      </p>
                    )}
                    {isAnnonce && annonceFree && (
                      <p className="text-[#888680] text-[11px] line-through mt-1">
                        {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(annoncePriceEur)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-5 flex justify-end">
                  <Button
                    onClick={() => handleBuy(item)}
                    disabled={isLoading}
                    data-testid={`buy-${item.lookup_key || item.ctaRoute || item.service}-btn`}
                    className={`${
                      item.highlighted
                        ? 'bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90'
                        : 'bg-[#1a1c1b] text-[#d9fb06] border border-[#d9fb06] hover:bg-[#d9fb06] hover:text-[#1a1c1b]'
                    } rounded-full px-6 py-2 text-sm font-bold uppercase transition-all disabled:opacity-60 disabled:cursor-not-allowed`}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Redirection…
                      </>
                    ) : isRoute ? (
                      item.ctaLabel || 'Découvrir'
                    ) : isFree ? (
                      'Réserver l\'appel gratuit'
                    ) : isCustom ? (
                      'Demander un devis'
                    ) : (
                      <>
                        <CreditCard className="mr-2 h-4 w-4" />
                        Acheter ce service
                      </>
                    )}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Payment Policy */}
        <div className="bg-[#302f2c] border border-[#3f4816] rounded-lg p-8 mb-12">
          <h3 className="text-2xl font-bold text-[#d9fb06] mb-6 uppercase text-center">
            Modalités de paiement
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-3">
              <Check className="text-[#d9fb06] flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="text-[#dfddd6] font-semibold mb-1">Paiement par carte bancaire</p>
                <p className="text-[#888680] text-sm">Sécurisé par Stripe — Visa, Mastercard, Amex</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Check className="text-[#d9fb06] flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="text-[#dfddd6] font-semibold mb-1">Confirmation immédiate</p>
                <p className="text-[#888680] text-sm">Reçu envoyé automatiquement par email</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Check className="text-[#d9fb06] flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="text-[#dfddd6] font-semibold mb-1">Prestation démarrée</p>
                <p className="text-[#888680] text-sm">Je vous recontacte sous 24h ouvrées</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Check className="text-[#d9fb06] flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="text-[#dfddd6] font-semibold mb-1">Devis personnalisé</p>
                <p className="text-[#888680] text-sm">Sur demande pour projets spécifiques</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            onClick={scrollToContact}
            data-testid="pricing-cta-btn"
            className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-10 py-6 text-lg font-bold uppercase transition-all hover:scale-105 shadow-lg"
          >
            Commencer gratuitement
          </Button>
          <p className="text-[#888680] mt-4">Appel découverte gratuit et sans engagement</p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
